import { addMonths, format, startOfMonth, subMonths } from "date-fns";

// Camelized `ProjectCostResponse` from GET /api/cost-and-usage/v1/projects/{uuid}/cost.
// Costs are Decimals, which the service serializes as strings.
export interface ProjectCostType {
  costType: string;
  cost: string;
}

export interface ProjectCostResponse {
  resourceType: string;
  resourceId: string;
  currency: string;
  total: string;
  costTypes: ProjectCostType[];
  earliestCostDate: string;
  latestCostDate: string;
}

// `cost` is null for a month the service has no cost data for.
export interface MonthlyProjectCost {
  month: string;
  cost: ProjectCostResponse | null;
}

export interface MonthRange {
  // yyyy-MM
  month: string;
  startDate: string;
  // Exclusive, matching the cost service's `end_date`.
  endDate: string;
}

const isoDate = (date: Date) => format(date, "yyyy-MM-dd");

// The `count` calendar months ending with the one containing `today`, oldest first.
export const monthRanges = (count: number, today: Date): MonthRange[] => {
  const currentMonth = startOfMonth(today);
  return Array.from({ length: count }, (_, i) => {
    const start = subMonths(currentMonth, count - 1 - i);
    return {
      month: format(start, "yyyy-MM"),
      startDate: isoDate(start),
      endDate: isoDate(addMonths(start, 1)),
    };
  });
};

// The cost types the cost pipeline emits, in their fixed color-slot order, so
// a cost type keeps its color whichever period is selected.
const KNOWN_COST_TYPES = ["compute-env", "ai-agent", "storage", "workflow"];

const COST_TYPE_LABELS: Record<string, string> = {
  "compute-env": "Compute environments",
  "ai-agent": "AI agents",
  storage: "Storage",
  workflow: "Workflows",
};

export const costTypeLabel = (costType: string) => COST_TYPE_LABELS[costType] ?? costType;

export const orderCostTypes = (costTypes: string[]): string[] => {
  const unique = [...new Set(costTypes)];
  const known = KNOWN_COST_TYPES.filter((costType) => unique.includes(costType));
  const unknown = unique.filter((costType) => !KNOWN_COST_TYPES.includes(costType)).sort();
  return [...known, ...unknown];
};

export interface MonthCostSummary {
  month: string;
  total: number;
  byType: Record<string, number>;
}

export interface ProjectCostSummary {
  total: number;
  currency: string | null;
  latestCostDate: string | null;
  // Cost types present in the period, in color-slot order.
  costTypes: string[];
  // Period total per cost type, largest first.
  byType: { costType: string; cost: number }[];
  months: MonthCostSummary[];
}

export const summarizeMonthlyCosts = (monthly: MonthlyProjectCost[]): ProjectCostSummary => {
  const periodByType: Record<string, number> = {};
  let currency: string | null = null;
  let latestCostDate: string | null = null;

  const months = monthly.map(({ month, cost }) => {
    const byType: Record<string, number> = {};
    for (const { costType, cost: amount } of cost?.costTypes ?? []) {
      byType[costType] = (byType[costType] ?? 0) + Number(amount);
      periodByType[costType] = (periodByType[costType] ?? 0) + Number(amount);
    }
    if (cost) {
      currency = cost.currency;
      if (latestCostDate === null || cost.latestCostDate > latestCostDate) {
        latestCostDate = cost.latestCostDate;
      }
    }
    const total = Object.values(byType).reduce((sum, amount) => sum + amount, 0);
    return { month, total, byType };
  });

  return {
    total: months.reduce((sum, month) => sum + month.total, 0),
    currency,
    latestCostDate,
    costTypes: orderCostTypes(Object.keys(periodByType)),
    byType: Object.entries(periodByType)
      .map(([costType, cost]) => ({ costType, cost }))
      .sort((a, b) => b.cost - a.cost),
    months,
  };
};
